import { action } from 'typesafe-actions';
import * as actionTypes from 'constants/action-types';
import { AnyAction } from 'redux';
import axios from 'axios/axios-base';
import { ApplicationState } from 'store/rootReducer';
import { hideToast, showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { BlazeMeterApiResponse, BlazemeterUserConfig } from 'types/rest/blazeMeterApiResponse';
import MixpanelService from 'utils/mixpanelService';
import { CREATE_SERVICE } from 'utils/mixpanelEvents';
import i18n from 'i18n';
import { Service } from 'types/rest/assetCatalogTypes';
import _ from 'lodash';
import { reloadWorkspaceComponents } from 'store/blazeUserConfig/blazeUserConfigActions';
import { formattedText } from 'utils/commonUtil';
import { getAllTransactionsAndCreateZipFileThunk } from '../transactions/transactionsActions';
import { getDataModelForService } from '../dataModel/dataModelActions';
import { confirm } from '../../components/ui/modal/BlazeConfirmationModal';
import React from 'react';
import { countEntitiesInDataModel } from '../../utils/blazeDataUtils';
import { getErrorMessage } from '../../utils/miscUtil';

/**
 * Updates user data.
 */
export const updateServices = (services: Service[]) => action(actionTypes.FETCH_SERVICES, services);

/**
 * Creates service and returns a promise which informs about the result.
 * @param serviceName
 */
export const createServiceThunk = (serviceName: string): ThunkAction<any, ApplicationState, null, AnyAction> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>, getState: any): Promise<Service> => {
    return new Promise<Service>((resolve, reject) => {
      const userConfig = getState().blazeUserConfig.blazeUserConfig as BlazemeterUserConfig;
      const service: Service = { name: serviceName, workspaceId: userConfig.user.activeWorkspaceId };

      axios
        .post('/workspaces/{workspaceId}/services', service)
        .then(result => {
          const response: BlazeMeterApiResponse = result.data;
          dispatch(
            showToastNotification(
              ToastType.Success,
              '',
              formattedText(i18n.t('successMsgs.serviceCreated'), serviceName),
            ),
          );
          resolve(response.result as Service);
        })
        .catch(error => {
          const errorMsg = _.get(error, 'response.data.error', error.toString());
          dispatch(
            showToastNotification(
              ToastType.Danger,
              '',
              i18n.t('errorMsgs.serviceCreationFailed', {
                name: serviceName,
                error: errorMsg,
              }),
            ),
          );
          reject(error.response ? error.response.data.error : 'Something went wrong.');
        });

      MixpanelService.track(CREATE_SERVICE());
    });
  };
};

/**
 * Fetch Services from backend. Results are cached for limited amount of time.
 * @param forceRefresh always fetches service list from backend when set to true. Returns cached list of services when false.
 */
export const fetchServices = (forceRefresh?: boolean): ThunkAction<any, ApplicationState, null, AnyAction> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>, getState: any): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      //Fetch services only in case they haven't been fetched recently (in 5 minutes)
      if (forceRefresh !== undefined && !forceRefresh) {
        const lastUpdate = getState().services.lastUpdate;
        if (lastUpdate != null && lastUpdate > Date.now() - 5 * 60 * 1000) {
          resolve();
        }
      }

      axios
        .get('/workspaces/{workspaceId}/services?limit=-1&sort=name')
        .then(result => {
          const response: BlazeMeterApiResponse = result.data;
          const services: Service[] = response.result;
          dispatch(updateServices(services));
          resolve();
        })
        .catch(error => {
          dispatch(
            showToastNotification(
              ToastType.Danger,
              i18n.t('errorMsgs.networkProblem'),
              i18n.t('errorMsgs.cannotLoadServices', {
                error: error.response ? error.response.data.error : 'Something went wrong.',
              }),
            ),
          );
          reject();
        });
    });
  };
};

export const deleteService = (services: Service[], service: Service, selectedServiceId: number | undefined) => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>, getState: any) => {
    try {
      await axios.delete(`/workspaces/{workspaceId}/services/${service.id}`);
      dispatch(
        showToastNotification(
          ToastType.Success,
          '',
          formattedText(i18n.t('servicePicker.successDelete'), service.name),
        ),
      );
      if (selectedServiceId !== service.id) {
        dispatch(reloadWorkspaceComponents(selectedServiceId));
      } else {
        const pageFilter = _.cloneDeep(getState().ui.pageFilter);
        pageFilter.serviceId = 0; // flag for -All Services-
        dispatch({
          type: actionTypes.SET_PAGE_FILTER,
          payload: pageFilter,
        });
        dispatch(reloadWorkspaceComponents(0));
      }
    } catch (error) {
      const errorMsg = _.get(error, 'response.data.error', error.toString());
      dispatch(showToastNotification(ToastType.Danger, `Could not delete service ${service.name}`, errorMsg));
    }
  };
};

export const exportService = (services: Service[], service: Service) => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>) => {
    if (service && service.id) {
      await dispatch(getAllTransactionsAndCreateZipFileThunk(service));
    }
  };
};

const triggerFileImport = async (service: Service, file: File, dispatch: any) => {
  // Create form data
  const formData = new FormData();
  formData.append('file', file);
  formData.append('force', 'true');
  try {
    const url = `/workspaces/{workspaceId}/services/${service.id}/import`;
    await axios.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'stream',
    });

    dispatch(showToastNotification(ToastType.Success, 'Import of Assets to the Service has been successful'));

    //Refresh everything
    dispatch(reloadWorkspaceComponents(service.id));
  } catch (e) {
    const errorMsg = getErrorMessage(e);
    dispatch(showToastNotification(ToastType.Danger, 'Service import has not finished successfully.', errorMsg));
  } finally {
    //Click outside closes the dropdown window
    document.dispatchEvent(new MouseEvent('click'));
  }
};

export const importService = (service: Service, file: File) => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>) => {
    const infoToast = dispatch(showToastNotification(ToastType.Info, 'Service import is in progress...'));
    if (service && service.id) {
      //Check if there is a blaze data model available
      const model = await getDataModelForService(service.id);
      const hasEntities = countEntitiesInDataModel(model) !== 0;

      if (hasEntities) {
        dispatch(hideToast(infoToast));
        confirm({
          confirmation: (
            <span>
              There is existing Service Data in the service <b>{service.name}</b>. By proceeding, the existing data will
              be overwritten by the imported data. Are you sure you want to continue?
            </span>
          ),
          yesOptionTitle: 'Override',
          title: 'Existing Service Data',
        }).then(async () => {
          await triggerFileImport(service, file, dispatch);
        });
      } else {
        await triggerFileImport(service, file, dispatch);
        dispatch(hideToast(infoToast));
      }
    }
  };
};
